import axiosClient from '@/api/axiosClient'

export interface ITestUserTemplate {
  id: string
  label: string
  description: string
  preset: Partial<ICreateTestUserPayload>
}

// ── KYC profiles ──────────────────────────────────────────────────────────────

export type KycDocumentType = 'licence' | 'passport' | 'medicare'

export interface IKycAddress {
  unitNumber?: string
  streetNumber: string
  streetName: string
  suburb: string
  state: string
  postcode: string
}

export interface IKycProfile {
  id: string
  documentType: KycDocumentType
  // Primary document number
  documentNumber: string
  // Secondary number (e.g. licence card number)
  secondaryNumber?: string
  // Expiry / validity
  expiry?: string
  // Medicare-specific
  positionOnCard?: number
  // Person details
  givenName: string
  middleName: string
  surname: string
  dateOfBirth: string
  address: IKycAddress
}

export const KYC_PROFILES: IKycProfile[] = [
  {
    id: 'frank_licence',
    documentType: 'licence',
    documentNumber: '701007813',
    secondaryNumber: 'P5552892',
    expiry: 'Any future date',
    givenName: 'Frank',
    middleName: 'P',
    surname: 'TestTwentyfive',
    dateOfBirth: '1974-01-01',
    address: { streetNumber: '38', streetName: 'Loch Valley Road', suburb: 'Noojee', state: 'VIC', postcode: '3833' },
  },
  {
    id: 'bruce_licence',
    documentType: 'licence',
    documentNumber: '833062880',
    secondaryNumber: 'P2291759',
    expiry: 'Any future date',
    givenName: 'Bruce',
    middleName: 'F',
    surname: 'Testnine',
    dateOfBirth: '1958-01-01',
    address: { streetNumber: '81', streetName: 'Argyle Road', suburb: 'Maryborough', state: 'VIC', postcode: '3465' },
  },
  {
    id: 'doris_passport',
    documentType: 'passport',
    documentNumber: '33333333',
    givenName: 'Doris',
    middleName: 'I',
    surname: 'TestSixteen',
    dateOfBirth: '1965-01-01',
    address: { streetNumber: '17', streetName: 'Arthur Street', suburb: 'Avoca', state: 'TAS', postcode: '7213' },
  },
  {
    id: 'james_medicare',
    documentType: 'medicare',
    documentNumber: '6603984391',
    positionOnCard: 1,
    expiry: '2030/01',
    givenName: 'James',
    middleName: 'A',
    surname: 'Testone',
    dateOfBirth: '1950-01-01',
    address: { unitNumber: '1', streetNumber: '35', streetName: 'Conn Street', suburb: 'Ferntree Gully', state: 'VIC', postcode: '3156' },
  },
]

// UI form fields — scenario flags are local only; kycProfileId is resolved to a kycDocument block before the API call
export interface ICreateTestUserPayload {
  firstName: string
  middleName: string
  lastName: string
  email: string
  mobile: string
  dateOfBirth: string
  password: string
  // Scenario flags — UI only, not sent to the API
  hasBankConnection: boolean
  hasLoan: boolean
  hasLoanApplication: boolean
  scenarioTag: string
  kycProfileId: string | null
  // Also call the generate-token endpoint right after creation — UI only, not sent to the API
  generateToken: boolean
}

// Maps to proto: enum DeviceType (shared.enum_prefix = "DEVICE_TYPE")
// Values are sent without the prefix since the gateway strips it
export const DeviceType = {
  UNSPECIFIED: 'UNSPECIFIED',
  IOS: 'IOS',
  WEB: 'WEB',
  ANDROID: 'ANDROID',
} as const

export type DeviceType = (typeof DeviceType)[keyof typeof DeviceType]

// Maps to proto: enum KycDocumentType in CreateUserManagementRequest
const KYC_DOCUMENT_TYPE_MAP: Record<KycDocumentType, string> = {
  licence: 'DRIVERS_LICENCE',
  passport: 'PASSPORT',
  medicare: 'MEDICARE',
}

// Shape sent to POST /management/users (proto: CreateUserManagementRequest)
interface ICreateTestUserApiRequest {
  firstName: string
  middleName?: string
  lastName: string
  email: string
  phoneNumber: string
  device: DeviceType
  password?: string
  kycDocument?: {
    type: string
    profileName: string
  }
}

// Shape returned from POST /management/users (proto: CreateUserManagementResponse)
interface ICreateTestUserApiResponse {
  data: {
    userId: string
  }
}

export interface ITestUser {
  id: string
  firstName: string
  lastName: string
  email: string
  mobile: string
  dateOfBirth: string
  scenarioTag: string
  createdAt: string
  environment: string
  // Populated locally when "Generate an auth token" was ticked on create
  token?: string
  tokenStatus?: 'pending' | 'error'
}

// Pre-built scenario templates developers can one-click fill from
export const TEST_USER_TEMPLATES: ITestUserTemplate[] = [
  {
    id: 'blank',
    label: 'Blank user',
    description: 'A fresh user with no history',
    preset: {
      hasBankConnection: false,
      hasLoan: false,
      hasLoanApplication: false,
      scenarioTag: 'blank',
    },
  },
  {
    id: 'with_bank',
    label: 'User with bank connection',
    description: 'User with an active bank connection already linked',
    preset: {
      hasBankConnection: true,
      hasLoan: false,
      hasLoanApplication: false,
      scenarioTag: 'bank_connected',
    },
  },
  {
    id: 'active_loan',
    label: 'User with active loan',
    description: 'User with a bank connection and an open loan',
    preset: {
      hasBankConnection: true,
      hasLoan: true,
      hasLoanApplication: false,
      scenarioTag: 'active_loan',
    },
  },
  {
    id: 'pending_application',
    label: 'Pending loan application',
    description: 'User in the middle of a loan application',
    preset: {
      hasBankConnection: true,
      hasLoan: false,
      hasLoanApplication: true,
      scenarioTag: 'pending_application',
    },
  },
  {
    id: 'declined',
    label: 'Declined applicant',
    description: 'User whose loan application was declined',
    preset: {
      hasBankConnection: true,
      hasLoan: false,
      hasLoanApplication: true,
      scenarioTag: 'declined_application',
    },
  },
]

// ── Update types (proto: UpdateUserManagementRequest / Response) ──────────────

// UI-level row — all fields present so form state is always defined
export interface IUpdateUserRow {
  rowId: string                      // local key only, not sent to API
  identifierType: 'userId' | 'email'
  identifierValue: string
  enableBankDetails: boolean
  accountNumber: string
  bsb: string
  autoGenerate: boolean
  enableRiskPercentile: boolean
  riskPercentile: string             // kept as string for controlled input
}

// Shape sent to PATCH /management/users (proto: UpdateUserManagementRequest)
interface IUpdateUsersApiRequest {
  data: Array<{
    userId?: string
    email?: string
    bankDetails?: {
      accountNumber: string
      bsb: string
      autoGenerate: boolean
    }
    riskPercentile?: {
      value: number
    }
  }>
}

// Shape returned from PATCH /management/users (proto: UpdateUserManagementResponse)
export interface IUpdateUsersApiResponseEntry {
  userId?: string
  email?: string
  bankDetails?: {
    accountNumber: string
    bsb: string
    autoGenerate: boolean
  }
  riskPercentile?: {
    value: number
  }
}

export interface IUpdateUsersApiResponse {
  data: IUpdateUsersApiResponseEntry[]
}

// ── Generate auth token ────────────────────────────────────────────────────────

export interface IGenerateTokenPayload {
  email: string
  password: string
}

// Shape returned from POST /management/users/token
interface IGenerateTokenApiResponse {
  accessToken: string
}

export const DeveloperService = {
  generateAuthToken: async (payload: IGenerateTokenPayload): Promise<string> => {
    const response = await axiosClient.post<IGenerateTokenApiResponse>(
      '/management/users/token',
      payload,
    )
    return response.data.accessToken
  },

  createTestUser: async (payload: ICreateTestUserPayload): Promise<ITestUser> => {
    const selectedProfile = payload.kycProfileId
      ? KYC_PROFILES.find(p => p.id === payload.kycProfileId) ?? null
      : null

    const body: ICreateTestUserApiRequest = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      phoneNumber: payload.mobile,
      device: DeviceType.WEB,
      ...(payload.middleName && { middleName: payload.middleName }),
      ...(payload.password && { password: payload.password }),
      ...(selectedProfile && {
        kycDocument: {
          type: KYC_DOCUMENT_TYPE_MAP[selectedProfile.documentType],
          profileName: selectedProfile.givenName,
        },
      }),
    }

    const response = await axiosClient.post<ICreateTestUserApiResponse>(
      '/management/users',
      body,
    )

    // Build a local ITestUser from the API response + original form data
    return {
      id: response.data.data.userId,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      mobile: payload.mobile,
      dateOfBirth: payload.dateOfBirth,
      scenarioTag: payload.scenarioTag,
      createdAt: new Date().toISOString(),
      environment: import.meta.env.MODE,
    }
  },

  updateTestUsers: async (rows: IUpdateUserRow[]): Promise<IUpdateUsersApiResponse> => {
    const body: IUpdateUsersApiRequest = {
      data: rows.map(row => {
        const entry: IUpdateUsersApiRequest['data'][number] = {
          [row.identifierType]: row.identifierValue,
        }

        if (row.enableBankDetails) {
          entry.bankDetails = row.autoGenerate
            ? { accountNumber: '', bsb: '', autoGenerate: true }
            : { accountNumber: row.accountNumber, bsb: row.bsb, autoGenerate: false }
        }

        if (row.enableRiskPercentile) {
          entry.riskPercentile = { value: Math.round(parseFloat(row.riskPercentile)) }
        }

        return entry
      }),
    }

    const response = await axiosClient.patch<IUpdateUsersApiResponse>('/management/users', body)
    return response.data
  },

  // No list/delete endpoints exist in the management API yet — these remain as stubs
  listTestUsers: async (): Promise<ITestUser[]> => {
    return []
  },

  deleteTestUser: async (_userId: string): Promise<void> => {
    // Not yet available in the management API
  },
}
